import { useState } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTrustDashboard } from '@/hooks/useTrustDashboard';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Building2, School, BarChart3 } from 'lucide-react';

type DateRange = '30d' | '90d' | '365d' | 'all';

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '365d': 'Last year',
  all: 'All time',
};

function getSince(range: DateRange): Date | null {
  if (range === 'all') return null;
  const d = new Date();
  if (range === '30d') d.setDate(d.getDate() - 30);
  else if (range === '90d') d.setDate(d.getDate() - 90);
  else if (range === '365d') d.setDate(d.getDate() - 365);
  return d;
}

function pct(n: number, total: number): string {
  if (total === 0) return '—';
  return `${Math.round((n / total) * 100)}%`;
}

function Metric({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div className="text-center">
      <p className="text-xl font-bold">{value ?? '—'}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}

export default function TrustDashboard() {
  const { profile } = useAuth();
  const [range, setRange] = useState<DateRange>('all');
  const since = getSince(range);
  const { data: schools, isLoading, error } = useTrustDashboard(since);

  const trustName = schools?.[0]?.trust_name ?? 'Your Trust';

  const totals = schools?.reduce(
    (acc, s) => ({
      assessments: acc.assessments + s.total_assessments,
      wts: acc.wts + s.wts_count,
      es: acc.es + s.es_count,
      gd: acc.gd + s.gd_count,
      teachers: acc.teachers + s.teacher_count,
    }),
    { assessments: 0, wts: 0, es: 0, gd: 0, teachers: 0 },
  );

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="h-5 w-5 text-primary" />
              <h1 className="text-2xl font-bold">{trustName}</h1>
            </div>
            <p className="text-muted-foreground text-sm">Trust-wide writing assessment overview</p>
          </div>

          {/* Date filter */}
          <div className="flex items-center gap-1 flex-wrap">
            {(Object.keys(DATE_RANGE_LABELS) as DateRange[]).map((r) => (
              <Button
                key={r}
                size="sm"
                variant={range === r ? 'default' : 'outline'}
                onClick={() => setRange(r)}
              >
                {DATE_RANGE_LABELS[r]}
              </Button>
            ))}
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
            Failed to load trust data. Ensure your account has the trust_leader role and is linked to a trust.
          </div>
        )}

        {!isLoading && !error && schools && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              <Card className="shadow-soft">
                <CardHeader className="pb-1 pt-4 px-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Schools</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <p className="text-3xl font-bold">{schools.length}</p>
                </CardContent>
              </Card>
              <Card className="shadow-soft">
                <CardHeader className="pb-1 pt-4 px-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Assessments</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <p className="text-3xl font-bold">{totals?.assessments ?? 0}</p>
                </CardContent>
              </Card>
              <Card className="shadow-soft bg-[hsl(38,92%,50%)]/10">
                <CardHeader className="pb-1 pt-4 px-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Working Towards</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <p className="text-3xl font-bold">{pct(totals?.wts ?? 0, totals?.assessments ?? 0)}</p>
                </CardContent>
              </Card>
              <Card className="shadow-soft bg-[hsl(142,71%,45%)]/10">
                <CardHeader className="pb-1 pt-4 px-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Expected</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <p className="text-3xl font-bold">{pct(totals?.es ?? 0, totals?.assessments ?? 0)}</p>
                </CardContent>
              </Card>
              <Card className="shadow-soft bg-[hsl(221,83%,53%)]/10">
                <CardHeader className="pb-1 pt-4 px-4">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Greater Depth</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <p className="text-3xl font-bold">{pct(totals?.gd ?? 0, totals?.assessments ?? 0)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Per-school comparison table */}
            {schools.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground text-sm">
                No schools found in your trust.
              </div>
            ) : (
              <Card className="shadow-soft">
                <CardHeader className="flex flex-row items-center gap-2 pb-3">
                  <BarChart3 className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base">School Comparison</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-muted-foreground text-xs uppercase tracking-wide">
                          <th className="text-left px-6 py-3 font-medium">School</th>
                          <th className="text-right px-4 py-3 font-medium">Assessments</th>
                          <th className="text-right px-4 py-3 font-medium">WTS</th>
                          <th className="text-right px-4 py-3 font-medium">ES</th>
                          <th className="text-right px-4 py-3 font-medium">GD</th>
                          <th className="text-right px-4 py-3 font-medium">Staff</th>
                          <th className="text-right px-4 py-3 font-medium">Syn. Maturity</th>
                          <th className="text-right px-4 py-3 font-medium">Txt. Cohesion</th>
                          <th className="text-right px-4 py-3 font-medium">Lex. Precision</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {schools.map((s) => (
                          <tr key={s.school_id} className="hover:bg-muted/30 transition-colors">
                            <td className="px-6 py-3 font-medium flex items-center gap-2">
                              <School className="h-4 w-4 text-muted-foreground shrink-0" />
                              {s.school_name}
                            </td>
                            <td className="text-right px-4 py-3 tabular-nums">{s.total_assessments}</td>
                            <td className="text-right px-4 py-3 tabular-nums text-[hsl(38,60%,45%)]">
                              {pct(s.wts_count, s.total_assessments)}
                            </td>
                            <td className="text-right px-4 py-3 tabular-nums text-[hsl(142,60%,35%)]">
                              {pct(s.es_count, s.total_assessments)}
                            </td>
                            <td className="text-right px-4 py-3 tabular-nums text-[hsl(221,60%,45%)]">
                              {pct(s.gd_count, s.total_assessments)}
                            </td>
                            <td className="text-right px-4 py-3 tabular-nums">{s.teacher_count}</td>
                            <td className="text-right px-4 py-3 tabular-nums">
                              {s.avg_syntactic_maturity ?? '—'}
                            </td>
                            <td className="text-right px-4 py-3 tabular-nums">
                              {s.avg_textual_cohesion ?? '—'}
                            </td>
                            <td className="text-right px-4 py-3 tabular-nums">
                              {s.avg_lexical_precision ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
